import React, { useState, useEffect } from 'react';
import {
  Form, Row, Col, Button, Alert, Spinner, InputGroup, Badge
} from 'react-bootstrap';
import { Save, X, Plus, Trash2, Ruler } from 'lucide-react';
import measurementService from '../../../services/measurementService';

/**
 * ✅ ATELIER: Formulaire de saisie d'une fiche de mesures.
 * Utilisé en création et en édition.
 */
const MeasurementForm = ({
  customerId,
  existingMeasurement = null,
  onSuccess,
  onCancel
}) => {
  const isEditing = !!existingMeasurement;

  // État du formulaire
  const [formData, setFormData] = useState(() => {
    const initial = {
      customerId,
      notes: '',
      customMeasurements: {}
    };
    measurementService.STANDARD_FIELDS.forEach(f => {
      initial[f.key] = '';
    });
    return initial;
  });

  // Liste des mesures personnalisées sous forme de tableau pour l'édition
  const [customList, setCustomList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Remplir le formulaire si édition
  useEffect(() => {
    if (existingMeasurement) {
      const data = {
        customerId,
        notes: existingMeasurement.notes || ''
      };

      measurementService.STANDARD_FIELDS.forEach(f => {
        data[f.key] = existingMeasurement[f.key] !== null && existingMeasurement[f.key] !== undefined
          ? String(existingMeasurement[f.key])
          : '';
      });

      setFormData(data);

      // Convertir custom en tableau pour UI
      if (existingMeasurement.customMeasurements) {
        const list = Object.entries(existingMeasurement.customMeasurements).map(
          ([key, value]) => ({ key, value: String(value) })
        );
        setCustomList(list);
      } else {
        setCustomList([]);
      }
    }
  }, [existingMeasurement, customerId]);

  const handleStandardChange = (key, value) => {
    // Autoriser les décimales (95.5)
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setFormData(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleNotesChange = (value) => {
    setFormData(prev => ({ ...prev, notes: value }));
  };

  const addCustomField = () => {
    setCustomList(prev => [...prev, { key: '', value: '' }]);
  };

  const updateCustomField = (index, field, value) => {
    setCustomList(prev =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  const removeCustomField = (index) => {
    setCustomList(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Construction du payload
    const payload = { ...formData };

    // Convertir la liste custom en objet, en ignorant les lignes vides
    const customObject = {};
    customList.forEach(item => {
      const cleanKey = item.key?.trim();
      const cleanValue = item.value?.trim();
      if (cleanKey && cleanValue) {
        const numValue = parseFloat(cleanValue);
        if (!isNaN(numValue)) {
          customObject[cleanKey] = numValue;
        }
      }
    });
    payload.customMeasurements = customObject;

    // Validation : au moins une mesure renseignée
    const hasStandard = measurementService.STANDARD_FIELDS.some(
      f => formData[f.key] && formData[f.key].trim() !== ''
    );
    const hasCustom = Object.keys(customObject).length > 0;

    if (!hasStandard && !hasCustom) {
      setError('Veuillez renseigner au moins une mesure');
      return;
    }

    setLoading(true);

    try {
      let response;
      if (isEditing) {
        response = await measurementService.updateMeasurement(
          existingMeasurement.id,
          payload
        );
      } else {
        response = await measurementService.createMeasurement(payload);
      }

      if (response.success) {
        console.log(`✅ Fiche ${isEditing ? 'modifiée' : 'créée'}`);
        onSuccess?.(response.data);
      } else {
        setError(response.message || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      console.error('❌ Erreur:', err);
      setError(err.message || 'Erreur inattendue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Mesures standards — 3 colonnes sur desktop, 2 sur tablette */}
      <div className="mb-4">
        <h6 className="mb-3 d-flex align-items-center gap-2">
          <Ruler size={16} className="text-primary" />
          Mesures standards
          <Badge bg="secondary" pill className="ms-2">
            12 champs
          </Badge>
        </h6>

        <Row className="g-3">
          {measurementService.STANDARD_FIELDS.map(field => (
            <Col key={field.key} md={4} sm={6}>
              <Form.Group>
                <Form.Label className="small mb-1">
                  {field.label}
                </Form.Label>
                <InputGroup size="sm">
                  <Form.Control
                    type="text"
                    inputMode="decimal"
                    placeholder="—"
                    value={formData[field.key]}
                    onChange={(e) => handleStandardChange(field.key, e.target.value)}
                  />
                  <InputGroup.Text>{field.unit}</InputGroup.Text>
                </InputGroup>
              </Form.Group>
            </Col>
          ))}
        </Row>
      </div>

      {/* Mesures personnalisées */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0 d-flex align-items-center gap-2">
            <Plus size={16} className="text-success" />
            Mesures personnalisées
            {customList.length > 0 && (
              <Badge bg="success" pill>{customList.length}</Badge>
            )}
          </h6>
          <Button
            variant="outline-success"
            size="sm"
            type="button"
            onClick={addCustomField}
          >
            <Plus size={14} className="me-1" />
            Ajouter
          </Button>
        </div>

        {customList.length === 0 ? (
          <div className="text-muted small text-center py-3 border rounded bg-light">
            Aucune mesure personnalisée. Cliquez sur "Ajouter" pour en créer une
            (ex: tour de cheville, hauteur pince poitrine...).
          </div>
        ) : (
          <div className="custom-measurements-list">
            {customList.map((item, index) => (
              <Row key={index} className="g-2 mb-2 align-items-center">
                <Col xs={6}>
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="Nom (ex: tour_cheville)"
                    value={item.key}
                    onChange={(e) => updateCustomField(index, 'key', e.target.value)}
                  />
                </Col>
                <Col xs={4}>
                  <InputGroup size="sm">
                    <Form.Control
                      type="text"
                      inputMode="decimal"
                      placeholder="Valeur"
                      value={item.value}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '' || /^\d*\.?\d*$/.test(v)) {
                          updateCustomField(index, 'value', v);
                        }
                      }}
                    />
                    <InputGroup.Text>cm</InputGroup.Text>
                  </InputGroup>
                </Col>
                <Col xs={2}>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    type="button"
                    onClick={() => removeCustomField(index)}
                    className="w-100"
                  >
                    <Trash2 size={14} />
                  </Button>
                </Col>
              </Row>
            ))}
          </div>
        )}
      </div>

      {/* Notes */}
      <Form.Group className="mb-4">
        <Form.Label>Notes (optionnel)</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          placeholder="Observations, préférences de la cliente, allergies..."
          value={formData.notes}
          onChange={(e) => handleNotesChange(e.target.value)}
        />
      </Form.Group>

      {/* Boutons actions */}
      <div className="d-flex justify-content-end gap-2 border-top pt-3">
        <Button
          variant="outline-secondary"
          type="button"
          onClick={onCancel}
          disabled={loading}
        >
          <X size={16} className="me-1" />
          Annuler
        </Button>
        <Button type="submit" variant="primary" disabled={loading}>
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Sauvegarde...
            </>
          ) : (
            <>
              <Save size={16} className="me-1" />
              {isEditing ? 'Mettre à jour' : 'Enregistrer'}
            </>
          )}
        </Button>
      </div>
    </Form>
  );
};

export default MeasurementForm;